/**
 * Flat AND-only ERP Filter JSON, moved verbatim from the dashboard builder.
 * Nested AND/OR lives in `tree.ts`.
 */
import { FILTER_OPS, isFilterOp, type FilterFieldKind, type FilterOp } from './operators';

export type FilterConditionDraft = {
  id: string;
  field: string;
  op: FilterOp;
  /** For scalar ops: single value string; for in: comma-separated */
  value: string;
};

/** Legacy UI/storage aliases → engine ops (hydration only). */
const LEGACY_OP_ALIASES: Record<string, FilterOp> = {
  starts_with: 'starts',
  ends_with: 'ends',
};

export function newConditionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyCondition(): FilterConditionDraft {
  return { id: newConditionId(), field: '', op: 'eq', value: '' };
}

/**
 * Build Filter scalar JSON from flat conditions (all AND-ed when >1).
 * Skips incomplete rows (empty field).
 * Returns undefined when nothing valid.
 */
export function conditionsToFilter(
  conditions: FilterConditionDraft[],
  kindFor?: (field: string) => FilterFieldKind | undefined,
): Record<string, unknown> | undefined {
  const parts: Record<string, unknown>[] = [];
  for (const c of conditions) {
    const part = draftToFilter(c, kindFor);
    if (part) parts.push(part);
  }
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return parts[0];
  return { and: parts };
}

/** One complete draft, or undefined when the row has no field or no value. */
export function draftToFilter(
  c: { field: string; op: string; value: string },
  kindFor?: (field: string) => FilterFieldKind | undefined,
): Record<string, unknown> | undefined {
  const field = c.field.trim();
  if (!field || !isFilterOp(c.op)) return undefined;
  const kind = kindFor?.(field);
  if (c.op === 'in') {
    const items = c.value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => coerceScalarForKind(s, kind));
    if (items.length === 0) return undefined;
    return { [field]: { in: items } };
  }
  const v = c.value.trim();
  if (v === '') return undefined;
  return { [field]: { [c.op]: coerceScalarForKind(v, kind) } };
}

/**
 * Best-effort parse of a simple Filter JSON into condition drafts.
 * Supports top-level field:{op} and {and:[...]} of those.
 * Hydrates engine ops (`starts`/`ends`) and legacy aliases (`starts_with`/`ends_with`).
 */
export function filterToConditions(filter: unknown): FilterConditionDraft[] {
  if (!filter || typeof filter !== 'object' || Array.isArray(filter)) return [];
  const obj = filter as Record<string, unknown>;
  if (Array.isArray(obj.and)) {
    return obj.and.flatMap((p) => filterToConditions(p));
  }
  const out: FilterConditionDraft[] = [];
  for (const [field, raw] of Object.entries(obj)) {
    const draft = fieldToDraft(field, raw);
    if (draft) out.push(draft);
  }
  return out;
}

/** One `{ field: { op: value } }` entry, or undefined when it is not a condition. */
export function fieldToDraft(field: string, raw: unknown): FilterConditionDraft | undefined {
  if (field === 'and' || field === 'or') return undefined;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const ops = raw as Record<string, unknown>;
  let matched: FilterOp | undefined;
  let rawKey: string | undefined;
  for (const op of FILTER_OPS) {
    if (op in ops) {
      matched = op;
      rawKey = op;
      break;
    }
  }
  if (!matched) {
    for (const [alias, canon] of Object.entries(LEGACY_OP_ALIASES)) {
      if (alias in ops) {
        matched = canon;
        rawKey = alias;
        break;
      }
    }
  }
  if (!matched || !rawKey) return undefined;
  if (matched === 'in' && Array.isArray(ops.in)) {
    return {
      id: newConditionId(),
      field,
      op: 'in',
      value: ops.in.map((x) => String(x)).join(', '),
    };
  }
  if (matched !== 'in') {
    return {
      id: newConditionId(),
      field,
      op: matched,
      value: String(ops[rawKey] ?? ''),
    };
  }
  return undefined;
}

/** Count complete (field + value) conditions for UI badges. */
export function countCompleteConditions(conditions: FilterConditionDraft[]): number {
  return conditions.filter((c) => {
    if (!c.field.trim()) return false;
    if (c.op === 'in') {
      return c.value
        .split(',')
        .map((s) => s.trim())
        .some(Boolean);
    }
    return c.value.trim() !== '';
  }).length;
}

/**
 * Hard input filter for number/decimal filter values: everything a keystroke
 * may leave behind. number = digits with an optional leading minus; decimal
 * additionally one dot with at most 4 fraction digits (ERP columns are
 * numeric(15,4)/(19,4)) — a typed comma becomes the dot. In list mode (`in`)
 * the comma is the LIST separator, so decimals there must use the dot and the
 * rules apply per comma segment.
 */
export function sanitizeNumericFilterInput(
  raw: string,
  kind: 'number' | 'decimal',
  list: boolean,
): string {
  const one = (seg: string): string => {
    const sign = seg.trimStart().startsWith('-') ? '-' : '';
    if (kind === 'number') {
      // Keep the user's list spacing — the wire split trims each item.
      return sign + seg.replace(list ? /[^\d ]/g : /[^\d]/g, '');
    }
    // decimal: the first separator wins as the dot, later ones are stripped.
    const cleaned = (list ? seg : seg.replace(/,/g, '.')).replace(
      list ? /[^\d. ]/g : /[^\d.]/g,
      '',
    );
    const dot = cleaned.indexOf('.');
    if (dot === -1) return sign + cleaned;
    const intPart = cleaned.slice(0, dot);
    const frac = cleaned.slice(dot + 1).replace(/[. ]/g, '').slice(0, 4);
    return `${sign}${intPart}.${frac}`;
  };
  // List mode: commas are separators — sanitize per segment, keep positions.
  if (!list) return one(raw);
  return raw.split(',').map(one).join(',');
}

function coerceScalar(raw: string): string | number | boolean {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (/^-?\d+$/.test(raw)) return Number(raw);
  if (/^-?\d+\.\d+$/.test(raw)) return Number(raw);
  return raw;
}

/** Integer string that survives an exact Number round-trip (rejects "007", > 2^53). */
export function roundTripInt(raw: string): number | null {
  if (!/^-?\d+$/.test(raw)) return null;
  const n = Number(raw);
  return String(n) === raw ? n : null;
}

/** FK record id from UI input: like roundTripInt, but trims and rejects signs (ids are unsigned). */
export function parseRecordId(raw: string): number | null {
  const t = raw.trim();
  return /^\d+$/.test(t) ? roundTripInt(t) : null;
}

/** Every comma-separated non-empty item is a round-trip-safe unsigned id.
 *  Blank / comma-only values count as an (empty) id list — nothing picked yet. */
export function isIdListValue(value: string): boolean {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .every((s) => parseRecordId(s) != null);
}

/** Order-preserving, deduped list of round-trip-safe unsigned ids; non-ids dropped. */
export function parseRecordIdList(raw: string): number[] {
  const out: number[] = [];
  for (const item of raw.split(',')) {
    const id = parseRecordId(item);
    if (id != null && !out.includes(id)) out.push(id);
  }
  return out;
}

function coerceScalarForKind(
  raw: string,
  kind: FilterFieldKind | undefined,
): string | number | boolean {
  if (kind === undefined) return coerceScalar(raw); // legacy path (free-text fields)
  switch (kind) {
    case 'number':
    case 'relation': {
      const n = roundTripInt(raw);
      return n ?? raw;
    }
    case 'boolean':
      if (raw === 'true') return true;
      if (raw === 'false') return false;
      return raw;
    case 'decimal': // keep precision: decimals stay strings (gridcatalog contract)
    case 'date':
    case 'timestamp':
    case 'text':
      return raw;
  }
}
